import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { DeviceIds, deviceType } from '@/core/environment/deviceType';
import { PageLayout } from '@/layout/PageLayout';
import { createUserService } from '@/services/userService';
import { isLoggedInSelector } from '@/stores/authStore';
import settingsState from '@/stores/settingsStore';
import {
  getDownloadAppCtaUrlOverride,
  getSafeDownloadUrl,
  isDownloadAppCtaExcluded,
  isDownloadAppCtaExperience,
} from './config';
import { DownloadAppCtaPage } from './DownloadAppCtaPage';

type Props = {
  homeLayout: React.ReactNode;
};

/* Read at module load: the homepage consumes the redirect flag, and `generateViralityParam` drops the query string shortly after. */
const searchAtLoad =
  typeof window === 'undefined' ? '' : window.location.search;
const wasRedirectedFromMobileAtLoad = isRedirectedFromMobile();

function isRedirectedFromMobile(): boolean {
  try {
    return createUserService().isRedirectedFromMobile();
  } catch {
    return false;
  }
}

/* Gated by the `mobile_download_app_cta` Janus setting (plus the QA switch); flows that own the homepage keep the standard home. */
export function DownloadAppCtaLayout({ homeLayout }: Props) {
  const settings = useRecoilValue(settingsState);
  const isLoggedIn = useRecoilValue(isLoggedInSelector);
  const isMobile = useMemo(() => deviceType() !== DeviceIds.Desktop, []);
  const urlOverride = useMemo(() => getDownloadAppCtaUrlOverride(), []);
  const isExcluded = isDownloadAppCtaExcluded({
    searches: [
      searchAtLoad,
      typeof window === 'undefined' ? '' : window.location.search,
    ],
    redirectedFromMobile:
      wasRedirectedFromMobileAtLoad || isRedirectedFromMobile(),
  });

  const experience = urlOverride ?? settings.values.mobileDownloadAppCta;
  const shouldRenderDownloadAppCta =
    settings.areReady &&
    isMobile &&
    !isLoggedIn &&
    !isExcluded &&
    isDownloadAppCtaExperience(experience);

  if (!shouldRenderDownloadAppCta) {
    return <>{homeLayout}</>;
  }

  return (
    <PageLayout title="Photo Enhancer">
      <DownloadAppCtaPage
        downloadUrl={getSafeDownloadUrl(
          settings.values.mobileDownloadAppCtaUrl
        )}
      />
    </PageLayout>
  );
}
